import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Zap, Shield, BarChart3, Globe, Star, Play, Layers } from "lucide-react";
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string; desc: string; product: string; city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const SaasTemplate = ({ name, desc, product, city, customizations, editMode = false, onCustomize }: Props) => {
  const c = customizations || {};
  const edit = (field: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(field, v);
  const displayName = c.heroTitle || name;
  const displayDesc = c.heroSubtitle || desc;
  const displayProduct = c.productName || product || "La solution";
  const displayCta = c.ctaText || "Démarrer gratuitement";
  return (
  <div className="bg-[#0f172a] text-[#f8fafc] min-h-screen">
    {/* Nav */}
    <nav className="fixed top-0 w-full z-50 bg-[#0f172a]/80 backdrop-blur-xl border-b border-blue-500/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Layers className="h-4 w-4 text-white" />
          </div>
          <EditableText as="span" value={displayName} onChange={edit("heroTitle")} editMode={editMode} className="text-lg font-bold" data-field="brand-name" />
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-slate-400">
          <span className="hover:text-white cursor-pointer">Produit</span>
          <span className="hover:text-white cursor-pointer">Tarifs</span>
          <span className="hover:text-white cursor-pointer">Ressources</span>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-5 rounded-full text-sm">Essai gratuit</Button>
        </div>
      </div>
    </nav>

    {/* Hero */}
    <section className="min-h-screen flex items-center pt-16 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(59,130,246,0.15),transparent)]" />
      <div className="relative max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium">
            <Zap className="h-3.5 w-3.5" /> Nouveau : IA intégrée
          </span>
          <h1 className="text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight">
            <EditableText as="span" value={displayProduct} onChange={edit("productName")} editMode={editMode} data-field="hero-title" /><br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">pour votre business</span>
          </h1>
          <EditableText as="p" value={displayDesc} onChange={edit("heroSubtitle")} editMode={editMode} multiline className="text-xl text-slate-400 max-w-xl mx-auto" data-field="hero-subtitle" />
          <div className="flex justify-center gap-4 pt-4">
            <Button className="bg-blue-600 hover:bg-blue-700 h-14 px-8 rounded-full text-base">
              <EditableText as="span" value={displayCta} onChange={edit("ctaText")} editMode={editMode} data-field="cta-primary" /> <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" className="h-14 px-8 rounded-full border-slate-600 text-slate-300 hover:bg-slate-800">
              <Play className="mr-2 h-4 w-4" /> Voir la démo
            </Button>
          </div>
          <div className="flex justify-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-1"><Check className="h-4 w-4 text-blue-500" />Gratuit 14 jours</span>
            <span className="flex items-center gap-1"><Check className="h-4 w-4 text-blue-500" />Sans carte bancaire</span>
            <span className="flex items-center gap-1"><Check className="h-4 w-4 text-blue-500" />Annulation libre</span>
          </div>
        </div>
      </div>
    </section>

    {/* Features */}
    <section className="py-24 bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold">Tout ce dont vous avez besoin</h2>
          <p className="text-slate-400 mt-3 text-lg">Une plateforme complète pour scaler votre activité.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: BarChart3, title: "Analytics avancés", desc: "Tableaux de bord en temps réel, rapports automatisés et insights IA." },
            { icon: Shield, title: "Sécurité maximale", desc: "Chiffrement bout-en-bout, conformité RGPD et audit trails." },
            { icon: Zap, title: "Automatisation", desc: "Workflows intelligents qui réduisent vos tâches répétitives de 80%." },
            { icon: Globe, title: "Multi-plateforme", desc: "Web, mobile et API — accessible partout, tout le temps." },
            { icon: Layers, title: "Intégrations", desc: "Connectez vos outils existants en quelques clics." },
            { icon: Star, title: "Support premium", desc: "Équipe dédiée avec temps de réponse garanti sous 2h." },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-8 hover:border-blue-500/30 transition-all group">
              <f.icon className="h-8 w-8 text-blue-500 mb-4" />
              <h3 className="text-lg font-bold mb-2">{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Pricing */}
    <section className="py-24">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold">Tarifs transparents</h2>
          <p className="text-slate-400 mt-3">Pas de frais cachés. Évoluez à votre rythme.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: "Starter", price: "29", features: ["5 utilisateurs", "Analytics basiques", "Support email", "1 Go stockage"] },
            { name: "Pro", price: "79", popular: true, features: ["25 utilisateurs", "Analytics avancés", "Support prioritaire", "50 Go stockage", "API access", "Intégrations"] },
            { name: "Enterprise", price: "Sur mesure", features: ["Illimité", "IA dédiée", "SLA garanti", "Stockage illimité", "Account manager", "Formation"] },
          ].map((plan) => (
            <div key={plan.name} className={`rounded-2xl p-8 ${plan.popular ? "bg-blue-600 text-white ring-2 ring-blue-400 scale-105" : "bg-slate-800/50 border border-slate-700/50"}`}>
              {plan.popular && <span className="text-xs font-bold uppercase tracking-wider text-blue-200 mb-4 block">Populaire</span>}
              <h3 className="text-xl font-bold">{plan.name}</h3>
              <p className="mt-2">
                <span className="text-4xl font-bold">{plan.price.includes("mesure") ? "" : plan.price + " EUR"}</span>
                {!plan.price.includes("mesure") && <span className="text-sm opacity-70">/mois</span>}
                {plan.price.includes("mesure") && <span className="text-lg font-semibold">{plan.price}</span>}
              </p>
              <ul className="mt-6 space-y-3">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm">
                    <Check className={`h-4 w-4 ${plan.popular ? "text-blue-200" : "text-blue-500"}`} />{f}
                  </li>
                ))}
              </ul>
              <Button className={`w-full mt-8 h-12 rounded-full ${plan.popular ? "bg-white text-blue-700 hover:bg-blue-50" : "bg-blue-600 hover:bg-blue-700 text-white"}`}>
                {plan.price.includes("mesure") ? "Nous contacter" : "Commencer"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Footer */}
    <footer className="border-t border-slate-800 py-12">
      <div className="max-w-7xl mx-auto px-6 text-center text-sm text-slate-500">
        © 2026 {name} — {city} — Propulsé par Grafton IA
      </div>
    </footer>
  </div>
  );
};

export default SaasTemplate;
